import { FC, useState } from "react"
import { MUIIcon, ProgressIndicator } from "../../../../../widgets"

import { AvatarText } from "../../../../components/avatar/AvatarText"
import { IPage } from "../../../models"
import { PageSettingsDialog } from "./PageSettingsDialog"
import { useAuth } from "../../../../../../modules/auth"
import { useToaster } from "../../../../../layout/toast"
import { useUpdatePageMetadataMutation } from "../../../../../services/data"

interface IDesignerPageHeaderProps {
    page: IPage
    onChange: (page: IPage) => void
}

export const DesignerPageHeader: FC<IDesignerPageHeaderProps> = ({ page, onChange }) => {
    const [showSettings, setShowSettings] = useState<boolean>(false)

    const handleSettingsOnClick = () => {
        setShowSettings(true)
    }

    return (
        <div className="d-flex flex-wrap gap-5">
            <PageHeader title={page.title || page.name} avatar={true} />
            <div className="d-flex gap-2 align-items-center">
                <ToolbarButton label="Settings" iconName="SettingsOutlined" onClick={handleSettingsOnClick} />
                <SaveToolbarButton page={page} label="Save" iconName="SaveOutlined" />

                <PageSettingsDialog
                    page={page}
                    show={showSettings}
                    hideModal={() => setShowSettings(false)}
                    onChange={onChange} />
            </div>
        </div>
    )
}

const PageHeader = ({ title, avatar }: { title: string, avatar?: boolean }) => {
    return (
        <div className="flex-fill d-flex align-items-center gap-5">
            {(avatar === true) && <>
                <AvatarText
                    label={title}
                    size={2}
                    random={false}
                    className="bg-primary fw-bolder" />
            </>}
            <h1 className="my-0 text-gray-700">Page Designer <span className="badge bg-info">{title}</span></h1>
        </div>
    )
}

interface IToolbarButtonProps {
    label: string
    iconName?: string
    onClick?: () => void
}

const ToolbarButton: FC<IToolbarButtonProps> = ({ label, iconName, onClick }) => {
    return (
        <button
            type="button"
            className="btn btn-sm btn-secondary py-1"
            onClick={onClick}>
            <div className="d-flex gap-1 align-items-center justify-content-center">
                {(!!iconName) && <><MUIIcon iconName={iconName} /></>}
                <span>{label}</span>
            </div>
        </button>
    )
}

interface ISaveToolbarButtonProps {
    page: IPage,
    label: string
    iconName?: string
}

const SaveToolbarButton: FC<ISaveToolbarButtonProps> = ({ page, label, iconName }) => {
    const [updatePageMetadata, { isLoading }] = useUpdatePageMetadataMutation();
    const { siteId } = useAuth()
    const { showToaster } = useToaster()

    const updatePage = () => {
        if (!page.portalId && siteId)
            updatePageMetadata({ ...page, portalId: siteId })
                .unwrap()
                .catch((err) => showToaster('Failed to update the page'))
        else
            updatePageMetadata(page)
                .unwrap()
                .catch((err) => showToaster('Failed to update the page'))
    }

    return (
        <button
            type="button"
            disabled={isLoading}
            className="btn btn-sm btn-primary py-1"
            onClick={updatePage}>
            <div className="d-flex gap-1 align-items-center justify-content-center">
                {(isLoading) ? <ProgressIndicator show={isLoading} /> : <>
                    {(!!iconName) && <><MUIIcon iconName={iconName} /></>}
                </>}
                <span>{label}</span>
            </div>
        </button>
    )
}
